import { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, FormControl, MenuItem, Select, TextField, Typography } from "@mui/material";
import { CommonDeleteButton } from "../../../../Components/Common Components/CommonComponents";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";
import ShowComponents from "../Common/ShowComponent";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";
import CommonValidation from "../../../../Components/CommonFunctions/CommonValidation";

const DocTreatmentPlan = ({ Title, ...props }) => {
    const { clinicId } = props;
    const [state, setState] = useState({
        TreatmentList: [],
        TreatmentPlanList: [],
        SelectedTreatment: null,
        eveningDuration: null,
        morningDuration: null,
        StartDate: "",
        EndDate: "",
        noDays: null,
        PatientType: "REGULAR",
        ScheduleRows: [],
        isTransmitted: false,
        SchedulerComment: "",
        isLoading: false,
        deletedIds: [],
    });

    const updateState = (obj) => setState((prev) => ({ ...prev, ...obj }));

    const getApiCall = useCallback(() => {
        getTreatmentList();
        getTreatmentPlans();
    }, [clinicId]);

    // ✅ Fetch treatment list
    const getTreatmentList = () => {
        RestAPIService.getAll(Serviceurls.AYURVEDA_TREATMENT_MASTER + `?clinic_id=${clinicId}`)
            .then((response) => {
                if (response.data.status === "success") {
                    updateState({ TreatmentList: response.data.data || [] });
                } else {
                    props.errorMessage(response?.data?.message);
                }
            })
            .catch((error) => props.errorMessage(error?.message));
    };

    // ✅ Fetch treatment plan
    const getTreatmentPlans = () => {
        const params = props?.isFromIp ? `?ip_admission_id=${props.ipAdmissionId}&clinic_id=${props.clinicId}&doctor_id=${props.doctorId}` : `?appointment=${props.appointmentId}`;
        RestAPIService.getAll(Serviceurls.AYURVEDA_TREATMENT_PLAN + params)
            .then((response) => {
                if (response.data.status === "success") {
                    const data = response.data.data;
                    if (data) {
                        updateState({
                            id: data.id,
                            TreatmentPlanList: response.data.data || [],
                            isTransmitted: data?.is_transmit || false,
                            morningDuration: data.morning_duration || null,
                            eveningDuration: data.evening_duration || null,
                            StartDate: data.start_date || "",
                            noDays: data.no_of_days || null,
                            PatientType: data.patient_type || "REGULAR",
                            SchedulerComment: data.scheduler_comment || "",
                            ScheduleRows: Array.isArray(data.vasthi_details)
                                ? data.vasthi_details.map((item) => ({
                                    id: item.id,
                                    frequency: item.frequency || "",
                                    unit: "Mins",
                                    morningDuration: item.morning_duration || null,
                                    eveningDuration: item.evening_duration || null,
                                    start_date: item.start_date || "",
                                    end_date: item.end_date || "",
                                    noDays: item.no_of_days || null,
                                }))
                                : [],
                        });
                    } else {
                        updateState({ TreatmentPlanList: [], ScheduleRows: [] });
                    }
                } else {
                    props.errorMessage(response?.data?.message);
                }
            })
            .catch((error) => props.errorMessage(error?.message));
    };

    const formatToIST = (dateObj) => {
        if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
            console.warn("Invalid date passed to formatToIST:", dateObj);
            return "";
        }
        const localTime = new Date(dateObj.toLocaleDateString("en-CA"));
        return localTime.toISOString().split("T")[0];
    };

    useEffect(() => {
        if (state.StartDate && state.noDays) {
            const start = new Date(state.StartDate);
            let totalDays = Number(state.noDays);

            // Calculate end date
            const end = new Date(start);
            end.setDate(start.getDate() + totalDays - 1);

            // Format to YYYY-MM-DD
            const formattedEndDate = formatToIST(end)

            updateState({ EndDate: formattedEndDate });
        } else {
            updateState({ EndDate: "" });
        }
    }, [state.noDays, state.StartDate]);

    // ✅ Save Treatment Plan
    const saveTreatmentPlan = () => {
        if (!state.morningDurationDuration || !state.eveningDuration) return props.errorMessage("At least one duration is required")
        if (!state.StartDate || !state.EndDate) return props.errorMessage("Dates are required");
        if (!state.noDays)
            return props.errorMessage("No Of Days is required", "error");

        const payload = {
            ...(state.id ? { id: state.id } : {}),
            ...(props?.isFromIp ? { ip_admission_id: props.ipAdmissionId, clinic_id: props.clinicId, doctor_id: props.doctorId } : { appointment: props?.appointmentId }),
            morning_duration: state.morningDuration,
            evening_duration: state.eveningDuration,
            start_date: state.StartDate,
            no_of_days: Number(state.noDays),
            patient_type: state.PatientType.toUpperCase(),
            scheduler_comment: state.SchedulerComment,
            vasthi_details: state.ScheduleRows.map((item) => ({
                frequency: item.frequency,
                morning_duration: item.morningDuration,
                evening_duration: item.eveningDuration,
                start_date: item.start_date,
                no_of_days: Number(item.noDays),
                ...(item.id ? { id: item.id } : {}),
            })),
            ...(state.id ? { deleted_ids: state.deletedIds || [] } : {})
        };

        updateState({ isLoading: true });
        const method = state.id ? "NewupdateWithOutId" : "create";
        RestAPIService[method](payload, Serviceurls.AYURVEDA_TREATMENT_PLAN)
            .then((response) => {
                if (response.data.status === "success") {
                    props.successMessage(response.data.message);
                    getTreatmentPlans();
                    updateState({
                        StartDate: "",
                        EndDate: "",
                        noDays: null,
                        morningDuration: null,
                        eveningDuration: null,
                        SchedulerComment: "",
                        ScheduleRows: [],
                        id: null,
                    });
                } else {
                    props.errorMessage(response?.data?.message);
                }
            })
            .catch((error) => props.errorMessage(error?.response?.data?.message || error?.message))
            .finally(() => updateState({ isLoading: false }));
    };

    const PostTransmitData = () => {
        const payload = {
            id: state.id,
            is_transmit: true,
            ...(props?.isFromIp ? { ip_admission_id: props.ipAdmissionId, clinic_id: props.clinicId, doctor_id: props.doctorId } : { appointment: props?.appointmentId }),
        };

        updateState({ isLoading: true });
        RestAPIService.create(payload, Serviceurls.AYURVEDA_TREATMENT_PLAN)
            .then((response) => {
                if (response.data.status === "success") {
                    props.successMessage(response.data.message);
                    getTreatmentPlans();
                } else {
                    props.errorMessage(response?.data?.message);
                }
            })
            .catch((error) => props.errorMessage(error?.message))
            .finally(() => updateState({ isLoading: false }));
    }

    // Add new schedule row
    const addScheduleRow = () => {
        const newRow = {
            id: null,
            frequency: "",
            morningDuration: null,
            eveningDuration: null,
            start_date: "",
            end_date: "",
            noDays: null,
        };
        updateState({ ScheduleRows: [...state.ScheduleRows, newRow] });
    };

    const updateScheduleRow = (index, key, value) => {
        const updatedRows = state.ScheduleRows.map((row, idx) => {
            if (idx === index) {
                let updated = { ...row, [key]: value };

                // Prevent start date before master start date
                if (key === "start_date") {
                    const masterStart = new Date(state.StartDate);
                    const selectedStart = new Date(value);

                    if (selectedStart < masterStart) {
                        props.errorMessage("Schedule start date cannot be before master start date");
                        updated.start_date = state.StartDate;
                    }
                }

                // --- CALCULATE END DATE ---
                if (["noDays", "morningDuration", "eveningDuration", "start_date", "frequency"].includes(key)) {
                    if (updated.start_date && updated.noDays) {
                        const start = new Date(updated.start_date);
                        const masterEnd = new Date(state.EndDate);
                        let totalDays = Number(updated.noDays);

                        if (totalDays > 0) {
                            let dayIncrement = 1;

                            switch (updated.frequency) {
                                case "ONCEINTWODAY":
                                    dayIncrement = 2;
                                    break;
                                case "ONCEINTHREEDAY":
                                    dayIncrement = 3;
                                    break;
                                default:
                                    dayIncrement = 1; // daily
                            }

                            // total calendar days = (sessions - 1) * interval
                            const totalCalendarDays = (totalDays - 1) * dayIncrement;

                            const end = new Date(start);
                            end.setDate(start.getDate() + totalCalendarDays);

                            if (end > masterEnd) {
                                props.errorMessage("Schedule end date cannot exceed master end date");
                                updated.end_date = "";
                            } else {
                                updated.end_date = formatToIST(end);
                            }
                        } else {
                            updated.end_date = "";
                        }
                    } else {
                        updated.end_date = "";
                    }
                }

                return updated;
            }
            return row;
        });

        // update first
        updateState({ ScheduleRows: updatedRows });

        // --- VALIDATE OVERLAPPING TIME SLOTS ---
        setTimeout(() => validateSlotOverlap(updatedRows), 50);
    };

    const validateSlotOverlap = (rows) => {
        for (let i = 0; i < rows.length; i++) {
            const r1 = rows[i];
            if (!r1.start_date || !r1.end_date) continue;

            const s1 = new Date(r1.start_date);
            const e1 = new Date(r1.end_date);

            for (let j = i + 1; j < rows.length; j++) {
                const r2 = rows[j];
                if (!r2.start_date || !r2.end_date) continue;

                const s2 = new Date(r2.start_date);
                const e2 = new Date(r2.end_date);

                const isOverlap = s1 <= e2 && s2 <= e1;

                if (!isOverlap) continue;

                // ---- MORNING OVERLAP CHECK ----
                if (r1.morningDuration && r2.morningDuration) {
                    props.errorMessage(`Morning slot overlaps between row ${i + 1} and row ${j + 1}`);

                    // Clear invalid duration
                    const updated = [...rows];
                    updated[j].morningDuration = "";

                    return updateState({ ScheduleRows: updated });
                }

                // ---- EVENING OVERLAP CHECK ----
                if (r1.eveningDuration && r2.eveningDuration) {
                    props.errorMessage(`Evening slot overlaps between row ${i + 1} and row ${j + 1}`);

                    const updated = [...rows];
                    updated[j].eveningDuration = "";

                    return updateState({ ScheduleRows: updated });
                }
            }
        }
    };

    return (
        <ShowComponents onIntersection={getApiCall.bind(this)}>
            <Card sx={{
                width: "100%", height: "fit-content", padding: "0.3vw",
                marginBottom: "0.5vw", border: `2px solid ${props.isSelected ? Colors.themeDark : Colors.white}`
            }}>
                {/* --- HEADER ROW (Title and SAVE Button) --- */}
                <Box display={"flex"} flexDirection={'row'} alignItems={"center"} justifyContent={'space-between'} padding={"0.5vw"} borderBottom={`1px solid #E3E2E2`}>
                    {renderTitle(Title || "Treatment Plan", props.isSelected)}
                    {props?.from !== "walkIn" ?
                        <Box display={state.isTransmitted ? "none" : "flex"}>
                            {state?.id && <Button sx={{ marginRight: "1vw" }} variant="contained" color="primary" onClick={PostTransmitData} disabled={state.isLoading}>
                                Transmit
                            </Button>}
                            <Button variant="contained" color="primary" onClick={saveTreatmentPlan} disabled={state.isLoading}>
                                {state.id ? "Update" : "SAVE"}
                            </Button>
                        </Box>
                        : null}
                </Box>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                    {/* === HEADER ROW === */}
                    <Box
                        display="grid"
                        gridTemplateColumns="10dvw 7dvw 7dvw 10.5dvw 6dvw 9dvw 9dvw 3dvw"
                        alignItems="center"
                        sx={{
                            backgroundColor: "#f3f3f3",
                            fontWeight: 600,
                            color: "#333",
                            py: "0.4dvw",
                            px: "0.5dvw",
                            borderBottom: "0.15dvw solid #ccc",
                            minWidth: "65dvw",
                        }}
                    >
                        <Typography variant="body2">Slot</Typography>
                        <Typography variant="body2">M.Duration</Typography>
                        <Typography variant="body2" textAlign="left">E.Duration</Typography>
                        <Typography variant="body2" textAlign="left">Start Date</Typography>
                        <Typography variant="body2" textalign="left">No Of Days</Typography>
                        <Typography variant="body2" textAlign="left">End Date</Typography>
                        <Typography variant="body2" textAlign="center">Patient Type</Typography>
                        <Typography variant="body2" textAlign="center">Action</Typography>
                    </Box>

                    {/* === MASTER ROW === */}
                    <Box
                        display="grid"
                        gridTemplateColumns="10dvw 7dvw 7dvw 10.5dvw 6dvw 9dvw 9dvw 3dvw"
                        alignItems="center"
                        sx={{
                            py: "0.5dvw",
                            px: "0.5dvw",
                            borderBottom: "0.1dvw solid #ddd",
                            minWidth: "65dvw",
                        }}
                    >
                        {/* Slot */}
                        <Typography fontSize="0.85vw" color="#444">
                            Regular
                        </Typography>

                        {/* Duration */}
                        <Box display="flex" alignItems="center" gap="0.4dvw">
                            <TextField
                                size="small"
                                type="number"
                                value={state.morningDuration}
                                disabled={state.isTransmitted}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "" || (/^\d{0,3}$/.test(val) && Number(val) <= 999)
                                    ) {
                                        updateState({ morningDuration: val });
                                    }
                                }}
                                inputProps={{ maxLength: 3 }}
                                InputProps={{
                                    endAdornment: (
                                        <span style={{ fontSize: "0.9rem", color: "#555" }}>Mins</span>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input": {
                                            paddingRight: ".5rem", // space for Mins
                                        },
                                    }
                                }}
                                sx={{
                                    width: "6.5dvw",
                                    "& input[type=number]": {
                                        "-moz-appearance": "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                    {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>
                        <Box display="flex" alignItems="center" gap="0.4dvw">
                            <TextField
                                size="small"
                                type="number"
                                value={state.eveningDuration}
                                disabled={state.isTransmitted}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (
                                        val === "" ||
                                        (/^\d{0,3}$/.test(val) && Number(val) <= 999)
                                    ) {
                                        updateState({ eveningDuration: val });
                                    }
                                }}
                                inputProps={{ maxLength: 3 }}
                                InputProps={{
                                    endAdornment: (
                                        <span style={{ fontSize: "0.9rem", color: "#555" }}>Mins</span>
                                    ),
                                    sx: {
                                        "& .MuiInputBase-input": {
                                            paddingRight: ".5rem", // space for Mins
                                        },
                                    }
                                }}
                                sx={{
                                    width: "6.5dvw",
                                    "& input[type=number]": {
                                        "-moz-appearance": "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                    {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>

                        {/* Start Date */}
                        <TextField
                            size="small"
                            type="date"
                            disabled={(!state.morningDuration && !state.eveningDuration) || state.isTransmitted}
                            value={state.StartDate}
                            onChange={(e) => updateState({ StartDate: e.target.value, noDays: null })}
                            sx={{ width: "10dvw" }}
                            inputProps={{
                                min: new Date().toISOString().split("T")[0], // restrict past dates
                            }}
                        />

                        {/* Session */}
                        <TextField
                            size="small"
                            type="text"
                            value={state.noDays}
                            disabled={state.isTransmitted}
                            onChange={(e) => {
                                const { value } = e.target;
                                const isNum = CommonValidation.numberOnly(value)
                                if (value === "" || isNum && (+value >= 0 && +value <= 999)) {
                                    updateState({ noDays: value })
                                }
                            }}
                            sx={{ width: "5dvw" }}
                        />

                        {/* End Date */}
                        <Typography sx={{ width: "8.5dvw" }}>{state.EndDate}</Typography>

                        {/* Patient Type */}
                        <FormControl size="small" sx={{ width: "8.5dvw" }}>
                            <Select
                                value={state.PatientType}
                                disabled={state.isTransmitted}
                                onChange={(e) => updateState({ PatientType: e.target.value, })}
                            >
                                <MenuItem value="REGULAR">Regular</MenuItem>
                                <MenuItem value="VULNERABLE">Vulnerable</MenuItem>
                            </Select>
                        </FormControl>


                        {/* Action */}
                        {props?.from !== "walkIn" ?
                            <Button
                                variant="contained"
                                onClick={addScheduleRow}
                                disabled={state.isTransmitted}
                                sx={{ minWidth: "3.5dvw", height: "2dvw" }}
                            >
                                +
                            </Button>
                            : null}
                    </Box>

                    {/* === SCHEDULE ROWS === */}
                    {state.ScheduleRows.map((sub, index) => (
                        <Box
                            key={`${sub.id}_${index}`}
                            display="grid"
                            gridTemplateColumns="10dvw 7dvw 7dvw 10.5dvw 6dvw 9dvw 9dvw 3dvw"
                            alignItems="center"
                            sx={{
                                py: "0.4dvw",
                                px: "0.5dvw",
                                borderBottom: "0.1dvw solid #eee",
                                minWidth: "65dvw",
                            }}
                        >
                            {/* Slot */}
                            <FormControl size="small" sx={{ width: "9.5dvw" }}>
                                <Select
                                    value={sub.frequency}
                                    disabled={state.isTransmitted}
                                    onChange={(e) => updateScheduleRow(index, "frequency", e.target.value)}
                                >
                                    <MenuItem value="DAILY">Every Day</MenuItem>
                                    <MenuItem value="ONCEINTWODAY">Once in Two Day</MenuItem>
                                    <MenuItem value="ONCEINTHREEDAY">Once in Three Day</MenuItem>
                                </Select>
                            </FormControl>


                            {/* Duration */}
                            <Box display="flex" alignItems="center" gap="0.4dvw">
                                <TextField
                                    size="small"
                                    type="number"
                                    value={sub.morningDuration}
                                    disabled={state.isTransmitted}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || (/^\d{0,3}$/.test(val) && Number(val) <= 999)
                                        ) {
                                            updateScheduleRow(index, "morningDuration", val);
                                        }
                                    }}
                                    inputProps={{ maxLength: 3 }}
                                    InputProps={{
                                        endAdornment: (
                                            <span style={{ fontSize: "0.9rem", color: "#555" }}>Mins</span>
                                        ),
                                        sx: {
                                            "& .MuiInputBase-input": {
                                                paddingRight: ".5rem", // space for Mins
                                            },
                                        }
                                    }}
                                    sx={{
                                        width: "6.5dvw",
                                        "& input[type=number]": {
                                            "-moz-appearance": "textfield",
                                        },
                                        "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                        {
                                            "-webkit-appearance": "none",
                                            margin: 0,
                                        },
                                    }}
                                />
                            </Box>
                            <Box display="flex" alignItems="center" gap="0.4dvw">
                                <TextField
                                    size="small"
                                    type="number"
                                    value={sub.eveningDuration}
                                    disabled={state.isTransmitted}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || (/^\d{0,3}$/.test(val) && Number(val) <= 999)) {
                                            updateScheduleRow(index, "eveningDuration", val);
                                        }
                                    }}
                                    inputProps={{ maxLength: 3 }}
                                    InputProps={{
                                        endAdornment: (
                                            <span style={{ fontSize: "0.9rem", color: "#555" }}>Mins</span>
                                        ),
                                        sx: {
                                            "& .MuiInputBase-input": {
                                                paddingRight: ".5rem", // space for Mins
                                            },
                                        }
                                    }}
                                    sx={{
                                        width: "6.5dvw",
                                        "& input[type=number]": {
                                            "-moz-appearance": "textfield",
                                        },
                                        "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                        {
                                            "-webkit-appearance": "none",
                                            margin: 0,
                                        },
                                    }}
                                />
                            </Box>

                            {/* Start Date */}
                            <TextField
                                size="small"
                                type="date"
                                value={sub.start_date}
                                inputProps={{
                                    min: state.StartDate,
                                    max: state.EndDate,
                                }}
                                disabled={state.isTransmitted}
                                onChange={(e) =>
                                    updateScheduleRow(index, "start_date", e.target.value)
                                }
                                sx={{ width: "10dvw" }}
                            />

                            {/* Session */}
                            <TextField
                                size="small"
                                type="number"
                                value={sub.noDays}
                                disabled={state.isTransmitted}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "" || (/^\d{0,3}$/.test(val) && Number(val) <= 999)) {
                                        updateScheduleRow(index, "noDays", e.target.value);
                                    }
                                }}
                                sx={{
                                    width: "5dvw",
                                    '& input[type=number]': { '-moz-appearance': 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                    },
                                }}
                            />

                            {/* End Date */}
                            <Typography sx={{ width: "8.5dvw" }}>{sub.end_date}</Typography>

                            {/* Empty to align with Patient Type */}
                            <Box></Box>

                            {/* Delete */}
                            {props?.from !== "walkIn" ?
                                <Box width={"3dvw"} display="flex" justifyContent="center" alignItems="center">
                                    <CommonDeleteButton
                                        disable={state.isTransmitted}
                                        onClick={() =>
                                            updateState({
                                                ScheduleRows: state.ScheduleRows.filter((_, i) => i !== index),
                                                deletedIds: sub?.id ? [...state.deletedIds, sub.id] : [...state.deletedIds],
                                            })
                                        }
                                    />
                                </Box>
                                : null}
                        </Box>
                    ))}
                </Box>
                {/* --- SCHEDULER COMMENT --- */}
                <Box sx={{ p: 1, borderTop: '1px solid #ddd', mt: 1 }}>
                    <Typography fontWeight="bold" fontSize="0.9vw" mb={0.5}>
                        Scheduler Comment
                    </Typography>
                    <TextField
                        multiline
                        rows={props?.from === "walkIn" ? 2 : 4}
                        disabled={state.isTransmitted}
                        fullWidth
                        value={state.SchedulerComment}
                        onChange={(e) => updateState({ SchedulerComment: e.target.value })}
                        variant="outlined"
                        size="small"
                    />
                </Box>
            </Card >
        </ShowComponents >
    );
};

export default DocTreatmentPlan;